import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom-v5-compat";
import {
  FlightSearchSelectors,
  FlightShopSelectors,
} from "@b2bportal/core-flights";
import { HtsAirCfarOffersSelectors } from "@b2bportal/core-flights-cfar-offers";
import {
  PackageType,
  ChosenProduct,
  type ChosenFlight,
} from "@b2bportal/core-packages";
import type { AppDispatch } from "@b2bportal/core-types";
import { getPackageRoutes } from "@b2bportal/core-utilities";
import { PACKAGES_QUERY_PARAM } from "@tenant/packages";
import { Experiments, useExperiment } from "@/modules/experiments/src";
import { getPricingMode } from "@/store/flights/shop/store";
import { PackagesBaseActions } from "@/store/packages/slice/packages";
import { PackageFlightsActions } from "@/store/packages/slice/packagesFlights";
import { PackagesSearchActions } from "@/store/packages/slice/packagesSearch";
import { setPackageUpSellParams } from "@/tracking/upSell/packageUpSellParams";
import { toUpSellPlacement } from "@/tracking/upSell/placement";
import {
  UpSellFailureReason,
  UpSellRawEventName,
  useEmitRbcRawEvent,
} from "@/tracking/upSellEmit";

type FlightSegmentSummary = NonNullable<ChosenFlight["outbound"]>;

export type FlightUpSellData = ChosenFlight & {
  handleAddStayClick: (childAges: number[]) => Promise<void>;
};

export const mapSliceToSegmentSummary = (
  slice: NonNullable<
    ReturnType<typeof FlightShopSelectors.getFlightsData>
  >["slices"][string]
): FlightSegmentSummary => {
  const firstSegment = slice.segments[0];
  return {
    arrival: slice.arrival,
    departure: slice.departure,
    flightNumber: firstSegment.flightNumber,
    marketingAirline: slice.marketingAirline,
    operatingAirline: firstSegment.operatingAirline,
    origin: slice.origin,
    stops: slice.stops,
    totalDurationMinutes: slice.totalDurationMinutes,
  };
};

/**
 * Returns the currently selected flight as FlightUpSellData, for use when
 * entering the packages funnel from the flight review screen.
 *
 * Returns null if required flight data is not yet available, or when the
 * selected fare bundles a Cancel For Any Reason offer: the package only
 * carries the flight (tripId + fareId), so the CFAR the traveller chose
 * would be dropped silently (RBC-2570). No entry point then.
 */
export const useFlightUpSellData = (): FlightUpSellData | null => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const selectedTrip = useSelector(FlightShopSelectors.selectedTripSelector);
  const flightsData = useSelector(FlightShopSelectors.getFlightsData);
  const origin = useSelector(FlightSearchSelectors.getOrigin);
  const destination = useSelector(FlightSearchSelectors.getDestination);
  const departureDate = useSelector(FlightSearchSelectors.getDepartureDate);
  const returnDate = useSelector(FlightSearchSelectors.getReturnDate);
  const adultsCount = useSelector(FlightSearchSelectors.getAdultsCount);
  const infantsOnLapCount = useSelector(
    FlightSearchSelectors.getInfantsOnLapCount
  );
  const infantsInSeatCount = useSelector(
    FlightSearchSelectors.getInfantsInSeatCount
  );
  const pricingMode = useSelector(getPricingMode);
  const selectedCfarOffer = useSelector(
    HtsAirCfarOffersSelectors.selectCfarOfferWithSelectedStatus
  );

  const { getExperimentVariant } = useExperiment();
  const emitFailed = useEmitRbcRawEvent(UpSellRawEventName.HotelUpSellFailed);
  const trackFailure = (failure_reason: UpSellFailureReason) =>
    emitFailed({
      properties: {
        failure_reason,
        up_sell_placement: toUpSellPlacement(
          getExperimentVariant(Experiments.UPSELL_STAYS_FROM_FLIGHTS.name)
        ),
        ...(pricingMode ? { rbc_price_tab: pricingMode } : {}),
        destination: destination?.label,
        departure_date: departureDate ?? undefined,
        return_date: returnDate ?? undefined,
      },
    });

  const {
    tripId,
    outgoingFareId,
    returnFareId,
    outgoingSliceId,
    returnSliceId,
  } = selectedTrip;

  if (!tripId || !flightsData?.slices || selectedCfarOffer != null) {
    return null;
  }

  const fareId = returnFareId ?? outgoingFareId;
  if (!fareId) {
    return null;
  }

  const outboundSlice = outgoingSliceId
    ? flightsData.slices[outgoingSliceId]
    : undefined;

  if (!outboundSlice) {
    return null;
  }

  const returningSlice = returnSliceId
    ? flightsData.slices[returnSliceId]
    : undefined;

  const handleAddStayClick = async (childAges: number[]) => {
    if (!origin || !destination || !departureDate || !returnDate) {
      trackFailure(UpSellFailureReason.missing_search_params);
      return;
    }

    const chosenFlight: ChosenFlight = {
      ChosenProduct: ChosenProduct.ChosenFlight,
      tripId,
      fareId,
      outbound: mapSliceToSegmentSummary(outboundSlice),
      ...(returningSlice && {
        returning: mapSliceToSegmentSummary(returningSlice),
      }),
    };

    // Reset stale package search state before navigating so that on re-entry
    // the hotel availability page starts fresh. Without this, a previous visit's
    // searchFormValues.origin/destination (and hotels store dateRange) persist
    // in Redux and cause the auto-search to overwrite the correct URL with old
    // values before initializePackages even starts.
    dispatch(PackagesSearchActions.resetPackagesSearch());
    // Package flight results from an earlier package belong to a different
    // package token. Clear them so "Change flight" from checkout fetches
    // flights for this package instead of reusing the old fare -> package map.
    dispatch(PackageFlightsActions.resetPackageFlightsShop());
    dispatch(PackagesBaseActions.setPendingChosenFlight(chosenFlight));

    const params = new URLSearchParams({
      [PACKAGES_QUERY_PARAM.origin]: origin.label,
      [PACKAGES_QUERY_PARAM.destination]: destination.label,
      [PACKAGES_QUERY_PARAM.fromDate]: departureDate,
      [PACKAGES_QUERY_PARAM.untilDate]: returnDate,
      [PACKAGES_QUERY_PARAM.adults]: String(adultsCount),
      [PACKAGES_QUERY_PARAM.roomsCount]: "1",
      [PACKAGES_QUERY_PARAM.packageType]: PackageType.HOTEL_FLIGHT,
    });

    for (const age of childAges) {
      params.append(PACKAGES_QUERY_PARAM.children, String(age));
    }
    if (infantsOnLapCount) {
      params.set(PACKAGES_QUERY_PARAM.infantsOnLap, String(infantsOnLapCount));
    }
    if (infantsInSeatCount) {
      params.set(
        PACKAGES_QUERY_PARAM.infantsInSeat,
        String(infantsInSeatCount)
      );
    }

    // Attribution rides the URL from here to package checkout.
    setPackageUpSellParams(params, {
      rbc_price_tab: pricingMode,
      flight_trip_id: tripId,
    });

    navigate(
      `${getPackageRoutes().FULL_ROUTES.HOTELS.AVAILABILITY}?${params.toString()}`
    );
  };

  return {
    ChosenProduct: ChosenProduct.ChosenFlight,
    tripId,
    fareId,
    outbound: mapSliceToSegmentSummary(outboundSlice),
    ...(returningSlice && {
      returning: mapSliceToSegmentSummary(returningSlice),
    }),
    handleAddStayClick,
  };
};
